import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { geocode } from '../api/geocode';
import { CAMERA } from '../config/constants';
import { useAlertsQuery } from '../hooks/useAlerts';
import { useIsNarrow } from '../hooks/useMediaQuery';
import { regionAround } from '../lib/geo';
import { mapController } from '../map/mapController';
import { openDetail, useAppStore } from '../store/appStore';
import type { Alert } from '../types/alerts';
import type { PlaceResult } from '../types/search';
import { Button, SectionHeader, Spinner, StatusMessage } from './ui';

const CHIPS = ['All', 'Warning', 'Watch', 'Advisory'] as const;
type Chip = (typeof CHIPS)[number];

/**
 * BottomSheetAlertsView: search field, category chips, Places (geocoder
 * results, shown when the chip is All) and the alert list with locate buttons.
 */
export function SearchPanel() {
  const narrow = useIsNarrow();
  const sheetDetent = useAppStore((s) => s.sheetDetent);
  const setSheetDetent = useAppStore((s) => s.setSheetDetent);
  const addSearchPin = useAppStore((s) => s.addSearchPin);
  const { data: alerts, isLoading, error } = useAlertsQuery();

  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<Chip>('All');
  const [places, setPlaces] = useState<PlaceResult[] | null>(null);
  const [placesQuery, setPlacesQuery] = useState('');
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState<string | null>(null);
  const [pendingPlace, setPendingPlace] = useState<PlaceResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const trimmed = query.trim();
  const showPlaces = trimmed !== '' && chip === 'All';

  // Clearing or changing the text invalidates the last geocode (no autocomplete with the Census geocoder).
  useEffect(() => {
    if (trimmed === '') {
      setPlaces(null);
      setPlacesError(null);
      setPlacesQuery('');
    }
  }, [trimmed]);

  const filteredAlerts = useMemo(() => filterAlerts(alerts ?? [], chip, trimmed), [alerts, chip, trimmed]);
  const placeResults = showPlaces && places && placesQuery === trimmed ? places : [];
  const hasAnyResults = filteredAlerts.length > 0 || placeResults.length > 0;

  const submitPlaces = async (e: FormEvent) => {
    e.preventDefault();
    if (!showPlaces) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setPlacesLoading(true);
    setPlacesError(null);
    try {
      const results = await geocode(trimmed, ac.signal);
      if (ac.signal.aborted) return;
      setPlaces(results);
      setPlacesQuery(trimmed);
    } catch (err) {
      if (ac.signal.aborted) return;
      setPlacesError(err instanceof Error ? err.message : 'Could not search places.');
      setPlaces([]);
      setPlacesQuery(trimmed);
    } finally {
      if (!ac.signal.aborted) setPlacesLoading(false);
    }
  };

  const onFocus = () => {
    if (narrow && sheetDetent === 'small') setSheetDetent('medium');
  };

  const locate = (alert: Alert) => {
    if (!alert.centroid) return;
    mapController.showRegion(regionAround(alert.centroid, CAMERA.locateAlertMeters));
  };

  const navigateTo = (place: PlaceResult) => {
    mapController.showRegion(regionAround(place.location, CAMERA.centerOnUserMeters));
    addSearchPin({ id: `pin-${Date.now()}-${place.id}`, title: place.title, subtitle: place.subtitle, location: place.location });
    setPendingPlace(null);
    if (narrow) setSheetDetent('small');
  };

  return (
    <div className="search-panel">
      <form className="search-field" onSubmit={submitPlaces} role="search">
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="search-icon">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={onFocus}
          placeholder="Search NWS Alerts and Locations."
          aria-label="Search NWS alerts and locations"
          enterKeyHint="search"
          autoComplete="off"
        />
        {query ? (
          <button type="button" className="search-clear" onClick={() => setQuery('')} aria-label="Clear search">
            ×
          </button>
        ) : null}
      </form>

      <div className="chips" role="tablist" aria-label="Alert type">
        {CHIPS.map((c) => (
          <button key={c} type="button" role="tab" aria-selected={chip === c} className={`chip ${chip === c ? 'is-active' : ''}`} onClick={() => setChip(c)}>
            {c}
          </button>
        ))}
      </div>
      <hr className="divider thin" />

      <div className="results">
        {showPlaces && placesQuery !== trimmed && !placesLoading ? (
          <div className="places-hint">
            Press Enter to search places for “{trimmed}”.
          </div>
        ) : null}
        {placesLoading ? <Spinner label="Searching places…" /> : null}
        {placesError ? <StatusMessage>Could not search places. {placesError}</StatusMessage> : null}

        {placeResults.length > 0 ? (
          <>
            <SectionHeader>Places</SectionHeader>
            <ul className="result-list">
              {placeResults.map((p) => (
                <li key={p.id}>
                  <button type="button" className="result-row" onClick={() => setPendingPlace(p)}>
                    <span className="result-tile result-tile-place" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24">
                        <path d="M12 22s7-7.1 7-12A7 7 0 0 0 5 10c0 4.9 7 12 7 12z" fill="currentColor" />
                        <circle cx="12" cy="10" r="2.8" fill="#fff" />
                      </svg>
                    </span>
                    <span className="result-text">
                      <span className="result-title">{p.title}</span>
                      {p.subtitle ? <span className="result-subtitle">{p.subtitle}</span> : null}
                    </span>
                    <span className="result-plus" aria-hidden="true">+</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {showPlaces && placesQuery === trimmed && !placesLoading && !placesError && places && places.length === 0 ? (
          <div className="places-hint">No places matched “{trimmed}”.</div>
        ) : null}

        {isLoading ? <Spinner label="Loading alerts…" /> : null}
        {error ? <StatusMessage>Failed to load alerts. {error instanceof Error ? error.message : ''}</StatusMessage> : null}

        {!isLoading && !error && !hasAnyResults && !placesLoading ? (
          <div className="empty-state">
            <svg width="34" height="34" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
              <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <div>No results found</div>
          </div>
        ) : null}

        {filteredAlerts.length > 0 ? (
          <>
            {placeResults.length > 0 ? <SectionHeader>Flood Data</SectionHeader> : null}
            <ul className="result-list">
              {filteredAlerts.map((a) => (
                <li key={a.id}>
                  <div className="result-row">
                    <span className="result-tile" aria-hidden="true">
                      <AlertRowIcon alert={a} />
                    </span>
                    <button type="button" className="result-text result-text-btn" onClick={() => openDetail({ kind: 'alert', alertId: a.id })}>
                      <span className="result-title">{a.event}</span>
                      <span className="result-subtitle">{a.areaDesc}</span>
                    </button>
                    <button type="button" className="locate-btn" onClick={() => locate(a)} aria-label={`Show ${a.event} on the map`} disabled={!a.centroid}>
                      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M21 3L3 10.5l8 2.5 2.5 8L21 3z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      {pendingPlace ? (
        <div className="modal-backdrop" onClick={() => setPendingPlace(null)} role="presentation">
          <div className="modal-card action-dialog" role="dialog" aria-modal="true" aria-labelledby="place-dialog-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="place-dialog-title" className="action-dialog-title">{pendingPlace.title}</h2>
            {pendingPlace.subtitle ? <p className="action-dialog-message">{pendingPlace.subtitle}</p> : null}
            <div className="action-dialog-buttons">
              <Button variant="default" disabled>
                Add as Notification Address
              </Button>
              <div className="action-dialog-note">Saved Addresses arrive in a later phase.</div>
              <Button variant="primary" onClick={() => navigateTo(pendingPlace)}>
                Navigate to Location
              </Button>
              <Button variant="ghost" onClick={() => setPendingPlace(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Chip filters by event substring; text filters description, headline and area case-insensitively. */
export function filterAlerts(alerts: Alert[], chip: Chip, text: string): Alert[] {
  let list = alerts;
  if (chip !== 'All') list = list.filter((a) => a.event.includes(chip));
  if (text === '') return list;
  const q = text.toLowerCase();
  return list.filter((a) => a.description.toLowerCase().includes(q) || a.headline.toLowerCase().includes(q) || a.areaDesc.toLowerCase().includes(q));
}

/** SearchResultRow icon: info circle yellow for Advisory, red triangle for warnings, orange bubble otherwise. */
function AlertRowIcon({ alert }: { alert: Alert }) {
  if (alert.event === 'Flood Advisory') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" style={{ color: '#fbc02d' }}>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 11v5M12 8v.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }
  if (alert.event === 'Flood Warning' || alert.event === 'Flash Flood Warning') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" style={{ color: '#d32f2f' }}>
        <path d="M12 3L22 21H2L12 3z" fill="currentColor" />
        <path d="M12 9v5M12 17.5v.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" style={{ color: '#f57c00' }}>
      <path d="M4 5h16v11H9l-5 4V5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 8v4M12 14v.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}
